export const BELT_ORDER = ['white', 'blue', 'purple', 'brown', 'black'];

export const beltNames: Record<string, string> = {
  white: 'Blanco',
  blue: 'Azul',
  purple: 'Morado',
  brown: 'Marrón',
  black: 'Negro',
};

export const beltColors: Record<string, { background: string; color: string; border: string }> = {
  white: { background: '#f8f8f8', color: '#161616', border: '#d8d8d8' },
  blue: { background: '#1459b8', color: '#ffffff', border: '#1459b8' },
  purple: { background: '#6f3bb7', color: '#ffffff', border: '#6f3bb7' },
  brown: { background: '#6a432b', color: '#ffffff', border: '#6a432b' },
  black: { background: '#111111', color: '#ffffff', border: '#111111' },
};

const gradeTargets: Record<number, number> = {
  0: 40,
  1: 60,
  2: 100,
  3: 150,
};

export interface GraduationProgress {
  currentClasses: number;
  targetClasses: number;
  percentage: number;
  nextBelt: string;
  nextStripes: number;
  objective: string;
  isReviewReady: boolean;
}

interface GraduationProgressUser {
  graduationHistory?: { classCountAtGraduation: number }[] | null;
  totalBookings?: number | null;
  stripes?: number | null;
  belt?: string | null;
}

export const formatFullDate = (value?: string | null) => {
  if (!value) return 'Sin registro';

  return new Date(value).toLocaleDateString(navigator.language, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
};

export const getGradeLabel = (stripes: number) => {
  if (stripes === 0) return 'Sin grados';
  return `${stripes}º grado`;
};

export const getBeltLabel = (belt: string) => beltNames[belt] ?? belt;

export const getGraduationLabel = (belt: string, stripes: number) =>
  `${getGradeLabel(stripes)} ${getBeltLabel(belt).toLowerCase()}`;

export const getEstimateText = (percentage: number, isReviewReady: boolean) => {
  if (isReviewReady) return 'Listo para revisión del instructor';
  if (percentage >= 100) return 'Listo para graduar';
  if (percentage >= 85) return 'Muy cerca del siguiente grado';
  if (percentage >= 65) return 'Cerca del siguiente grado';
  if (percentage >= 35) return 'Avanzando con buen ritmo';
  return 'Construyendo base para el siguiente grado';
};

export const getProgress = ({
  classesSinceLastGraduation,
  stripes,
  belt,
}: {
  classesSinceLastGraduation: number;
  stripes: number;
  belt: string;
}): GraduationProgress => {
  if (stripes >= 4) {
    return {
      currentClasses: 0,
      targetClasses: 0,
      percentage: 100,
      nextBelt: belt,
      nextStripes: 4,
      objective: 'Revisión manual para cinturón',
      isReviewReady: true,
    };
  }

  const targetClasses = gradeTargets[stripes] ?? 0;
  const currentClasses = Math.max(classesSinceLastGraduation, 0);
  const percentage = targetClasses
    ? Math.min(Math.round((currentClasses / targetClasses) * 100), 100)
    : 100;
  const nextStripes = Math.min(stripes + 1, 4);

  return {
    currentClasses,
    targetClasses,
    percentage,
    nextBelt: belt,
    nextStripes,
    objective: getGraduationLabel(belt, nextStripes),
    isReviewReady: false,
  };
};

export const getUserGraduationProgress = (user: GraduationProgressUser): GraduationProgress => {
  const graduationHistory = user.graduationHistory ?? [];
  const totalBookings = user.totalBookings ?? 0;
  const latestGraduation = graduationHistory[0];
  const classesSinceLastGraduation = latestGraduation
    ? Math.max(totalBookings - latestGraduation.classCountAtGraduation, 0)
    : totalBookings;

  return getProgress({
    classesSinceLastGraduation,
    stripes: user.stripes ?? 0,
    belt: user.belt ?? 'white',
  });
};
